import { Component, ElementRef, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import * as ClaimTrackerModels from '@claim-tracker/common/models';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { debounceTime, distinctUntilKeyChanged, fromEvent, takeWhile } from 'rxjs';

import { expandCollapseAnimation } from '../shared/animations/expand-collapse.animation';
import { fadeInOutAnimation } from '../shared/animations/fade-in-out.animation';
import { inputErrorAnimation } from '../shared/animations/input-error.animation';
import { PageLevelMessageService } from '../shared/messages/page-level-message/page-level-message.service';
import { LoginRequestForm } from './data/login-request-form.interface';
import { LoginService } from './services/login.service';
import { loginFormValidator } from './validation/validation.util';

/**
 * Component for entry to log in a user with a claim number.
 *
 * @author Jesse Bloomster e020902
 */
@Component({
	selector: 'claim-tracker-entry',
	standalone: true,
	imports: [ReactiveFormsModule, NgxMaskDirective],
	providers: [LoginService, provideNgxMask({ validation: false })],
	templateUrl: './entry.component.html',
	styleUrls: ['./entry.component.css'],
	animations: [fadeInOutAnimation(), expandCollapseAnimation({ delayEnterMS: 300 }), inputErrorAnimation]
})
export class EntryComponent implements OnInit, OnDestroy {
	readonly FORM_INPUT_DEBOUNCE_TIME = 200;

	form: FormGroup<LoginRequestForm>;
	loading = signal(false);
	validationFailed = false;
	alive = true;

	/**
	 * Get the claim number form control.
	 */
	get claimNumberControl() {
		return this.form.controls.claimNumber;
	}

	/**
	 * Get the claim number form control errors.
	 */
	get claimNumberControlErrors() {
		return Object.values<string>(this.claimNumberControl.errors ?? {});
	}

	/**
	 * Get the name form control.
	 */
	get nameControl() {
		return this.form.controls.name;
	}

	/**
	 * Get the name form control errors.
	 */
	get nameControlErrors() {
		return Object.values<string>(this.nameControl.errors ?? {});
	}

	/**
	 * Get the phone number form control.
	 */
	get phoneNumberControl() {
		return this.form.controls.phoneNumber;
	}

	/**
	 * Get the phone number form control errors.
	 */
	get phoneNumberControlErrors() {
		return Object.values<string>(this.phoneNumberControl.errors ?? {});
	}

	/**
	 * @param loginService the service to attempt a user login
	 * @param messagesService the global page level messages service
	 * @param router the Angular Router
	 * @param elementRef the element reference
	 */
	constructor(
		private loginService: LoginService,
		private messagesService: PageLevelMessageService,
		private router: Router,
		private elementRef: ElementRef
	) {}

	/**
	 * Called when the component is initialized.
	 */
	ngOnInit() {
		this.form = new FormGroup<LoginRequestForm>(
			{
				claimNumber: new FormControl<string>('', {
					nonNullable: true,
					validators: [loginFormValidator('claimNumber')]
				}),
				name: new FormControl<string>('', { nonNullable: true, validators: [loginFormValidator('name')] }),
				phoneNumber: new FormControl<string>('', {
					nonNullable: true,
					validators: [loginFormValidator('phoneNumber')]
				})
			},
			{ updateOn: 'submit' }
		);

		// clear validation errors once inputs are updated
		this.elementRef.nativeElement.querySelectorAll('input').forEach((inputElement: HTMLInputElement) => {
			fromEvent<InputEvent>(inputElement, 'input')
				.pipe(
					debounceTime(this.FORM_INPUT_DEBOUNCE_TIME),
					distinctUntilKeyChanged('data'),
					takeWhile(() => this.alive)
				)
				.subscribe(() => {
					this.form.get(inputElement.id)?.setErrors(null);
				});
		});

		setTimeout(() => {
			this.elementRef.nativeElement.querySelector('#claimNumber').focus();
		});
	}

	/**
	 * Called when the component is destroyed.
	 */
	ngOnDestroy() {
		this.alive = false;
	}

	/**
	 * Attempt to track a claim with the given authentication details.
	 */
	trackClaim() {
		// reset validation indicator
		this.validationFailed = false;

		// if the form is invalid, mark all fields as touched to trigger error message visibility; don't submit form
		if (this.form.invalid) {
			// due to some controls having masks, we need to update their validity again in case mask cleared input
			this.claimNumberControl.updateValueAndValidity();
			this.phoneNumberControl.updateValueAndValidity();

			this.form.markAllAsTouched();
			this.validationFailed = true;
			return;
		}

		this.loading.set(true);

		const formValue = this.form.getRawValue();
		const updatedForm = new ClaimTrackerModels.com.aoins.claims.login.LoginRequest(
			formValue.claimNumber,
			formValue.name.replaceAll(/\W/g, ''),
			formValue.phoneNumber.replaceAll(/\D/g, '')
		);

		this.loginService.login(updatedForm).subscribe({
			next: () => {
				this.messagesService.clearPageLevelMessages();
				this.router.navigateByUrl('claim-details');
			},
			complete: () => this.loading.set(false)
		});
	}
}
